export interface IErroIntegracao {
  motivo: string;
  produto: string | null;
  texto: string;
}

// O Protheus responde em Windows-1252 e o ESF-API lê a resposta como UTF-8:
// cada letra acentuada chega já trocada por "�" (U+FFFD) e o byte original
// se perdeu no banco. Cada "�" é exatamente uma letra acentuada, então dá pra
// reconstruir as palavras pelo vocabulário das mensagens do Protheus.
const PERDIDO = "�";

const PALAVRAS_ACENTUADAS = [
  "não", "são", "há", "já", "até", "está", "estão", "será", "só", "após",
  "também", "área", "usuário", "código", "número", "série", "válido",
  "inválido", "obrigatório", "disponível", "possível", "armazém", "endereço",
  "preço", "crédito", "débito", "mínimo", "máximo", "período", "conteúdo",
  "parâmetro", "parâmetros", "cálculo", "referência", "sequência", "vigência",
  "pendência", "divergência", "transferência", "município", "saída", "único",
  "última", "último", "físico", "contábil", "lógico",
];

const DICIONARIO: Record<string, string> = {};
PALAVRAS_ACENTUADAS.forEach((palavra) => {
  DICIONARIO[palavra.replace(/[^a-z]/g, PERDIDO)] = palavra;
});

// Terminações sem ambiguidade (operação, restrição, informações, emissão, padrões).
const TERMINACOES: [RegExp, string][] = [
  [/��o$/, "ção"],
  [/��es$/, "ções"],
  [/([^aeiou�])�o$/, "$1ão"],
  [/([^aeiou�])�es$/, "$1ões"],
];

function aplicarCaixa(original: string, corrigida: string): string {
  const letras = original.replace(/[^A-Za-z]/g, "");
  if (letras.length > 1 && letras === letras.toUpperCase()) return corrigida.toUpperCase();
  if (/^[A-Z]/.test(original)) return corrigida[0].toUpperCase() + corrigida.slice(1);
  return corrigida;
}

export function corrigirAcentosProtheus(texto: string): string {
  if (!texto.includes(PERDIDO)) return texto;
  return texto.replace(/[A-Za-z�]*�[A-Za-z�]*/g, (token) => {
    const minuscula = token.toLowerCase();
    let corrigida = DICIONARIO[minuscula];
    if (!corrigida) {
      const regra = TERMINACOES.find(([re]) => re.test(minuscula));
      if (regra) corrigida = minuscula.replace(regra[0], regra[1]);
    }
    // Palavra fora do vocabulário (ou "�" sozinho, que pode ser "é" ou "à")
    // fica como veio — melhor um "�" do que um acento inventado.
    if (!corrigida || corrigida.includes(PERDIDO)) return token;
    return aplicarCaixa(token, corrigida);
  });
}

// Traduz o retorno do TOTVS (payload_recebido) num motivo legível.
// Só é usado quando situacao === 'ERRO_INTEGRACAO'.
export function parseErroIntegracao(payload: any): IErroIntegracao | null {
  if (!payload) return null;

  let p: any = payload;
  if (typeof p === "string") {
    try {
      p = JSON.parse(p);
    } catch {
      return { motivo: "Erro na integração com o TOTVS", produto: null, texto: corrigirAcentosProtheus(p) };
    }
  }

  const errArr = p?.data?.[0]?.error;
  const texto: string = corrigirAcentosProtheus(
    Array.isArray(errArr)
      ? errArr.join("\n")
      : String(p?.error || p?.message || JSON.stringify(p))
  );

  const isClienteNaoCadastrado = /A410NCLIE/.test(texto);

  let motivo = "Erro na integração com o TOTVS";
  if (/REGBLOQ/.test(texto) && /SF4/.test(texto)) {
    motivo = "TES BLOQUEADA no Protheus (SF4 - Tipos de Entrada e Saída)";
  } else if (/REGBLOQ/.test(texto) && /SB1/.test(texto)) {
    motivo = "PRODUTO BLOQUEADO no Protheus (SB1 - cadastro do produto)";
  } else if (isClienteNaoCadastrado) {
    // A filial faz parte do motivo: o mesmo cliente (código + loja) integra
    // na 0101 e é recusado na 0301 (NeuroVasc), para onde vai a parte
    // desmembrada do pedido. Sem ela o erro parece falso — o cliente "já tem
    // pedido saindo", só que pela outra filial.
    const filial = texto.match(/C5_FILIAL\s*:=\s*(\d{4})/);
    motivo = filial
      ? `CLIENTE NÃO CADASTRADO na filial ${filial[1]} do Protheus (A410NCLIE)`
      : "CLIENTE NÃO CADASTRADO no Protheus (A410NCLIE)";
  } else if (/RESTPRD/.test(texto)) {
    const localRestrito = texto.match(/local de estoque\s*'([^']+)'/i);
    const local = localRestrito ? ` '${localRestrito[1]}'` : "";
    motivo = `LOCAL DE ESTOQUE${local} BLOQUEADO para o usuário da integração no Protheus (RESTPRD)`;
  } else if (/A410VZ/.test(texto)) {
    motivo = "TES EM BRANCO - o Protheus não encontrou a TES do item";
  } else if (/nao encontrado na base/i.test(texto)) {
    motivo = "PRODUTO NÃO CADASTRADO no Protheus";
  } else if (/Internal Server Error/i.test(texto)) {
    motivo = "Falha interna do integrador (erro 500, sem detalhe do TOTVS)";
  }

  // Código do produto que o Protheus apontou no bloco de erro. Erro de cliente
  // não tem produto — sem esse guard, o "< -- Invalido" da linha C5_CLIENTE
  // seria lido como se fosse o código do produto.
  const matchProduto = isClienteNaoCadastrado ? null :
    texto.match(/([0-9]{6,})\s*<\s*-- Invalido/) ||
    texto.match(/C6_PRODUTO\s*:=\s*([0-9]{6,})/) ||
    texto.match(/base de dados:\s*([0-9]+)/);
  const produto: string | null = matchProduto ? matchProduto[1] : null;

  return { motivo, produto, texto };
}
